import { routes } from '@/config/routes'
import type { ConnectorType } from '@/config/wagmi'
import { useAccountContext } from '@/context/account'
import CoinbaseWalletLogo from '@/images/wallets/coinbaseWallet.png'
import RainbowWalletLogo from '@/images/wallets/rainbow.png'
import SafeWalletLogo from '@/images/wallets/safe.png'
import WalletConnectLogo from '@/images/wallets/walletConnect.png'
import { cn } from '@/utils/cn'
import { type ReactNode, createContext, useContext, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { type Connector, useConnectors } from 'wagmi'
import { Button, type ButtonProps } from '../common/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogContentWrapper,
  DialogHeader,
  DialogScrollArea,
  DialogTitle,
  DialogTrigger,
} from '../common/dialog'
import { Separator } from '../common/separator'
import { ArrowExpandedIcon } from '../icons/arrow'
import { CoinbaseWalletIcon } from '../icons/coinbaseWallet'
import { WalletButton } from './walletButton'

type WalletAction = 'create' | 'connect'

interface Props {
  action: WalletAction
  className?: string
  triggerVariant?: ButtonProps['variant']
  onClick?: () => void
}

export function ConnectModal({ action, className, triggerVariant, onClick }: Props) {
  const { isModalOpen, setOpenModal } = useConnectWalletModalContext()
  const isCreate = action === 'create'
  const [step, setStep] = useState<'action' | 'connector'>('action')
  const connectors = useConnectors()
  const uniqueConnectors = connectors.filter(
    (connector) => connector.name !== 'injected' && connector.icon !== undefined
  )

  return (
    <Dialog
      open={isModalOpen === action}
      onOpenChange={(open) => {
        setOpenModal(open ? action : undefined)
        if (!open) setStep('action')
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant={triggerVariant ? triggerVariant : isCreate ? 'secondary' : 'primary'}
          size="md"
          className={cn('px-3 text-sm xl:px-5', className)}
          onClick={() => onClick?.()}
        >
          {isCreate ? 'Create wallet' : 'Connect wallet'}
        </Button>
      </DialogTrigger>
      <DialogContent position="right" className="max-w-lg">
        <DialogBody>
          <DialogScrollArea>
            <div className={cn(step === 'action' ? 'block' : 'hidden')}>
              <SelectActionStep isCreate={isCreate} handleNextStep={() => setStep('connector')} />
            </div>
            <div className={cn(step === 'connector' ? 'block' : 'hidden')}>
              <SelectConnectorStep connectors={uniqueConnectors} handlePrevStep={() => setStep('action')} />
            </div>
          </DialogScrollArea>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

interface ActionStepProps {
  isCreate: boolean
  handleNextStep: () => void
}

const SelectActionStep = ({ isCreate, handleNextStep }: ActionStepProps) => {
  const coinbaseWalletConnector = useGetConnectorByType('coinbaseWallet')
  const walletConnectConnector = useGetConnectorByType('walletConnect')

  return (
    <DialogContentWrapper className="pt-10 md:pt-16">
      <DialogHeader>
        <DialogTitle>{isCreate ? 'Create wallet' : 'Connect wallet'}</DialogTitle>
      </DialogHeader>
      <div className={cn('flex gap-4 md:gap-5', isCreate ? 'flex-col-reverse' : 'flex-col')}>
        <Button
          variant={isCreate ? 'input' : 'primary'}
          className={cn(actionButtonStyles, isCreate && 'bg-page text-heading-foreground')}
          onClick={handleNextStep}
        >
          <div className="mx-auto flex flex-col items-center gap-2">
            <div
              className={cn('-space-x-2 flex w-fit items-center', isCreate ? '[&>div]:bg-surface' : '[&>div]:bg-page')}
            >
              <WalletPreviewIcon name={coinbaseWalletConnector.name} icon={CoinbaseWalletLogo} />
              <WalletPreviewIcon name={walletConnectConnector.name} icon={WalletConnectLogo} />
              <WalletPreviewIcon name="Rainbow" icon={RainbowWalletLogo} />
              <WalletPreviewIcon name="SAFE" icon={SafeWalletLogo} />
            </div>
            <span className="text-sm">+more</span>
          </div>
          <span className="flex items-center gap-2">Connect Wallet</span>
        </Button>
        <div className="flex items-center gap-3">
          <Separator className="w-auto flex-grow" />
          <span className="text-nowrap text-center text-xs">{isCreate ? 'Already' : 'Do not'} have a wallet?</span>
          <Separator className="w-auto flex-grow" />
        </div>
        <WalletButton
          variant={isCreate ? 'primary' : 'input'}
          connector={coinbaseWalletConnector}
          className={cn(actionButtonStyles, !isCreate && 'bg-page text-heading-foreground')}
        >
          <CoinbaseWalletIcon className="h-7" />
          <span className="flex items-center gap-2">Create Coinbase Smart Wallet</span>
        </WalletButton>
      </div>
      <p className="text-center text-sm">
        By connecting a wallet, you accept vaults.fyi{' '}
        <Button variant="default" size="default" className="rounded-full text-accent underline" asChild>
          <Link to={routes.terms} target="_blank" rel="noreferrer">
            Terms of Service
          </Link>
        </Button>{' '}
        and consent to{' '}
        <Button variant="default" size="default" className="rounded-full text-accent underline" asChild>
          <Link to={routes.privacy} target="_blank" rel="noreferrer">
            Privacy Policy
          </Link>
        </Button>
        .
      </p>
    </DialogContentWrapper>
  )
}

interface WalletPreviewIconProps {
  name: string
  icon: string
}

const WalletPreviewIcon = ({ name, icon }: WalletPreviewIconProps) => {
  return (
    <div className="overflow-hidden rounded-full p-2">
      <img src={icon} alt={name} className="size-6 shrink-0 rounded-full" />
    </div>
  )
}

interface ConnectorStepProps {
  connectors: Connector[]
  handlePrevStep: () => void
}

const SelectConnectorStep = ({ connectors, handlePrevStep }: ConnectorStepProps) => {
  const coinbaseWalletConnector = useGetConnectorByType('coinbaseWallet')
  const walletConnectConnector = useGetConnectorByType('walletConnect')
  return (
    <DialogContentWrapper className="pt-10 md:pt-16">
      <DialogHeader className="flex items-center gap-3">
        <Button variant="input" className="size-10 shrink-0 bg-page" onClick={handlePrevStep}>
          <ArrowExpandedIcon direction="left" size={24} />
          <span className="sr-only">Return</span>
        </Button>
        <DialogTitle>Options</DialogTitle>
      </DialogHeader>
      {connectors.length > 0 && (
        <>
          <h3 className="font-semibold text-heading-foreground">Available:</h3>
          <ul className="flex flex-col gap-4 md:gap-5">
            {connectors.map((connector) => (
              <li key={connector.id} className="w-full">
                <ConnectorButton connector={connector} />
              </li>
            ))}
          </ul>
        </>
      )}
      <h3 className="font-semibold text-heading-foreground">Popular:</h3>
      <ul className="flex flex-col gap-4 md:gap-5">
        <li key="popular-coinbase" className="w-full">
          <ConnectorButton connector={coinbaseWalletConnector} icon={CoinbaseWalletLogo} />
        </li>
        <li key="popular-walletconnect" className="w-full">
          <ConnectorButton connector={walletConnectConnector} icon={WalletConnectLogo} />
        </li>
        <li key="popular-rainbow" className="w-full">
          <ConnectorButton connector={walletConnectConnector} name="Rainbow" icon={RainbowWalletLogo} />
        </li>
        <li key="popular-safe" className="w-full">
          <ConnectorButton connector={walletConnectConnector} name="SAFE" icon={SafeWalletLogo} />
        </li>
      </ul>
    </DialogContentWrapper>
  )
}

const actionButtonStyles = cn('flex h-fit min-h-32 flex-col gap-3 rounded-2xl p-5 text-base sm:text-xl')

const ConnectorButton = ({ connector, name, icon }: { connector: Connector; name?: string; icon?: string }) => {
  return (
    <WalletButton
      variant="input"
      connector={connector}
      className={cn(
        'flex h-14 w-full items-center justify-start gap-3 rounded-2xl px-5',
        'bg-page font-medium text-base'
      )}
    >
      <img src={icon ?? connector.icon} alt={name ?? connector.name} className="size-7 shrink-0 rounded-full" />
      <span>{name ?? connector.name}</span>
    </WalletButton>
  )
}

const ConnectWalletModalContext = createContext<{
  isModalOpen: WalletAction | undefined
  setOpenModal: React.Dispatch<React.SetStateAction<WalletAction | undefined>>
}>({
  isModalOpen: undefined,
  setOpenModal: undefined as unknown as React.Dispatch<React.SetStateAction<WalletAction | undefined>>,
})

export function useConnectWalletModalContext() {
  return useContext(ConnectWalletModalContext)
}

export function ConnectWalletModalProvider({ children }: { children: ReactNode }) {
  const { account } = useAccountContext()
  const [isModalOpen, setOpenModal] = useState<WalletAction | undefined>()

  useEffect(() => {
    account !== undefined && setOpenModal(undefined)
  }, [account])

  return (
    <ConnectWalletModalContext.Provider value={{ isModalOpen, setOpenModal }}>
      {children}
    </ConnectWalletModalContext.Provider>
  )
}

function useGetConnectorByType(type: ConnectorType) {
  const connectors = useConnectors()
  const connector = connectors.find((connector) => connector.type === type)
  if (!connector) throw new Error(`Connector with type ${type} not found`)
  return connector
}
